
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search as SearchIcon } from "lucide-react";

interface SearchFormProps {
  initialQuery: string;
  onSearch: (query: string) => void;
  isSearching: boolean;
}

const SearchForm = ({ initialQuery, onSearch, isSearching }: SearchFormProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle Enter key in the entire form component (not just the input)
      if (e.key === 'Enter' && query.trim()) {
        onSearch(query);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [query, onSearch]);

  return (
    <>
      <div className="mb-6">
        <Button 
          variant="outline" 
          onClick={handleGoBack}
          className="mb-4"
          aria-label="Go back"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        <h2 className="text-3xl font-bold tracking-tight text-nalc-dark dark:text-white">Universal Search</h2>
        <p className="text-muted-foreground mt-1">Find students, teachers, programs, and administrators</p>
      </div>

      <Card className="mb-8">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="flex gap-4">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, email, department, role..."
                value={query}
                onChange={handleInputChange}
                className="pl-10"
                autoFocus
              />
            </div>
            <Button 
              type="submit" 
              disabled={!query.trim() || isSearching}
              className="bg-nalc-teal hover:bg-nalc-teal/90"
            >
              Search
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  );
};

export default SearchForm;
